import type { CockpitRepRow } from '../../../types';
import { formatDollars } from '../../../lib/snapshotMetrics';
import { ACTIVITY_TYPE_COLUMNS, typeCount } from './activityTypes';

/**
 * The two money headers follow whatever window the table is showing (CRM-121).
 * They used to be hardcoded "$ This SY" / "$ Last SY" even when the user was
 * looking at — and exporting — the calendar-YTD view, so the file said one thing
 * and the numbers in it meant another.
 */
export interface RepCsvMoneyHeaders {
  current: string;
  prior: string;
}

const DEFAULT_MONEY_HEADERS: RepCsvMoneyHeaders = { current: '$ Current', prior: '$ Prior' };

/** Every column from both table tabs, so one export always means the same thing. */
function headers(money: RepCsvMoneyHeaders): string[] {
  return [
    'Rep', money.current, money.prior, 'YoY %', 'Fundraisers',
    'Activities', ...ACTIVITY_TYPE_COLUMNS.map((c) => c.label),
    'Onsite', 'Verified', 'Unverified', 'Flagged', 'Touched', 'Overdue', 'Conversions', 'People', 'Accounts', 'Last Seen',
  ];
}

function csvCell(v: string | number): string {
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function repRowsToCsv(rows: CockpitRepRow[], money: RepCsvMoneyHeaders = DEFAULT_MONEY_HEADERS): string {
  const lines = [headers(money).join(',')];
  for (const r of rows) {
    const cells: Array<string | number> = [
      r.displayName ?? r.userId,
      formatDollars(r.raisedThisSyCents),
      formatDollars(r.raisedPriorSyCents),
      r.fundraiserYoyPct === null ? 'New' : `${r.fundraiserYoyPct}%`,
      r.campaignsThisSy, r.activitiesTotal,
      ...ACTIVITY_TYPE_COLUMNS.map((c) => typeCount(r, c.type)),
      r.onsite, r.onsiteVerified, r.onsiteUnverified, r.onsiteFlagged, r.contactsTouched,
      r.overdue, r.conversions, r.peopleOwned, r.namedAccountsOwned,
      r.lastSeen ? r.lastSeen.slice(0, 10) : 'Never',
    ];
    lines.push(cells.map(csvCell).join(','));
  }
  return lines.join('\n');
}
